import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ShoppingBag } from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";
import { ASSETS } from "@/lib/data";
import { scrollToId } from "@/lib/scroll";
import { useCart } from "@/context/CartContext";

const LINKS = [
  { label: "About", id: "about" },
  { label: "Gallery", to: "/gallery" },
  { label: "Tickets", to: "/tickets" },
  { label: "Merchandise", id: "merch" },
  { label: "FAQ", to: "/faq" },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { count, setOpen: setCartOpen } = useCart();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToId(id), 450);
    } else {
      scrollToId(id);
    }
  };

  const goTo = (to) => {
    setOpen(false);
    navigate(to);
    window.scrollTo(0, 0);
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
  };

  const handleClick = (l) => (l.to ? goTo(l.to) : go(l.id));

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-void/80 backdrop-blur-xl border-b border-neon-violet/30" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-8">
        <button onClick={() => go("top")} className="flex items-center gap-3" data-testid="header-logo-btn">
          <img src={ASSETS.navLogo} alt="Pahlawan Championship" className="h-8 w-auto object-contain sm:h-10" />
        </button>

        <nav className="hidden items-center gap-8 md:flex">
          {LINKS.map((l) => (
            <button
              key={l.label}
              onClick={() => handleClick(l)}
              className="font-mono-cap text-[11px] text-slate-300 transition-colors hover:text-neon-cyan"
              data-testid={`nav-${l.id || l.label.toLowerCase()}`}
            >
              {l.label}
            </button>
          ))}
          <button
            onClick={() => go("event")}
            className="group relative overflow-hidden rounded-full border border-neon-pink/60 px-5 py-2 font-mono-cap text-[11px] text-white glow-ring"
            data-testid="header-tickets-btn"
          >
            <span className="relative z-10">Get Tickets</span>
            <span className="absolute inset-0 -z-0 translate-y-full bg-gradient-to-r from-neon-pink to-neon-violet transition-transform duration-300 group-hover:translate-y-0" />
          </button>
          <button
            onClick={() => setCartOpen(true)}
            className="relative text-slate-200 transition-colors hover:text-neon-cyan"
            data-testid="header-cart-btn"
            aria-label="Cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-neon-pink px-1 font-mono text-[9px] font-bold text-white" data-testid="cart-count">
                {count}
              </span>
            )}
          </button>
        </nav>

        <div className="flex items-center gap-4 md:hidden">
          <button onClick={() => setCartOpen(true)} className="relative text-white" data-testid="mobile-cart-btn" aria-label="Cart">
            <ShoppingBag className="h-5 w-5" />
            {count > 0 && (
              <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-neon-pink px-1 font-mono text-[9px] font-bold text-white">
                {count}
              </span>
            )}
          </button>
          <button className="text-white" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-neon-violet/20 bg-void/95 backdrop-blur-xl md:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {LINKS.map((l) => (
                <button
                  key={l.label}
                  onClick={() => handleClick(l)}
                  className="rounded-lg px-3 py-3 text-left font-sub text-sm text-slate-200 hover:bg-white/5"
                  data-testid={`mobile-nav-${l.id || l.label.toLowerCase()}`}
                >
                  {l.label}
                </button>
              ))}
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
